/**
 * =============================================================================
 * Componente: PhoneInput
 * Versão: 1.1.0 — Correção do bug de "dígito extra" no value externo
 *
 * Mudanças v1.1.0 (cirúrgicas):
 *   - handlePhoneChange agora deriva os dígitos do `value` externo a partir
 *     da string JÁ MASCARADA por applyPhoneMask (que respeita o limite de
 *     11 dígitos para BR ou os limites dos números especiais). Antes, o
 *     `cleanDigits` era extraído direto do raw input (sem limite), o que
 *     fazia com que colagens, autofill, IME mobile, autocorrect ou eventos
 *     duplicados pudessem enviar 12+ dígitos ao backend mesmo com o input
 *     visual mostrando apenas 11.
 *   - handleCountryCodeChange usa a mesma técnica (extrai dígitos do
 *     phoneNumber JÁ mascarado).
 *   - useEffect de sincronização: se o `value` externo trouxer mais dígitos
 *     do que a máscara permite, o componente emite onChange retroativo para
 *     "consertar" o estado do parent (autocorreção de valores corrompidos
 *     vindos de cadastros antigos / paste / etc.).
 *
 * Componente de input de telefone com seletor de código de país.
 * Suporta detecção automática de números especiais brasileiros:
 *   - 0800: ligação gratuita (0800-XXX-XXXX)
 *   - 4003: tarifa local (4003-XXXX)
 *   - 400X/300X: atendimento (XXXX-XXXX)
 * Para números especiais, o seletor de país é ocultado e o valor é salvo
 * SEM o código de país (+55).
 * =============================================================================
 */

import * as React from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { countryCodes, extractCountryCode } from '@/lib/country-codes';
import { applyPhoneMask, isSpecialPhoneNumber } from '@/lib/input-masks';
import { cn } from '@/lib/utils';

interface PhoneInputProps {
  /** Valor completo do telefone (ex: +5511999999999 ou 08001234567) */
  value?: string;
  /** Callback chamado quando o valor muda - retorna telefone completo */
  onChange?: (value: string) => void;
  /** Placeholder do campo de número */
  placeholder?: string;
  /** Se o campo está desabilitado */
  disabled?: boolean;
  /** Classes CSS adicionais para o container */
  className?: string;
  /** ID do input para acessibilidade */
  id?: string;
  /**
   * Quando true, o seletor de DDI exibe APENAS a bandeira (sem "+55"),
   * liberando espaço para o input do número. O dropdown continua mostrando
   * bandeira + código completo. Default: false.
   */
  compactSelector?: boolean;
  /** Acessibilidade: marca o campo como inválido */
  'aria-invalid'?: boolean;
}

/**
 * Helper interno: a partir de um valor cru (raw), aplica a máscara e
 * devolve a tupla (mascarado, dígitosSeguros).
 *
 * `dígitosSeguros` é EXATAMENTE o que está visível para o usuário — nunca
 * mais que o limite imposto por applyPhoneMask (11 para BR, 8/11 para
 * especiais). Esta é a fonte da verdade que vai ao onChange.
 */
function maskAndExtractSafeDigits(raw: string): {
  masked: string;
  safeDigits: string;
} {
  const masked = applyPhoneMask(raw);
  const safeDigits = masked.replace(/\D/g, '');
  return { masked, safeDigits };
}

export function PhoneInput({
  value = '',
  onChange,
  placeholder = '(11) 99999-9999',
  disabled = false,
  className,
  id,
  compactSelector = false,
  ...rest
}: PhoneInputProps) {
  // Extrai o código de país e número local do valor atual
  const { countryCode, localNumber } = React.useMemo(
    () => extractCountryCode(value),
    [value]
  );

  // Estados internos para controle do seletor e input
  const [selectedCode, setSelectedCode] = React.useState(countryCode);
  const [phoneNumber, setPhoneNumber] = React.useState(
    applyPhoneMask(localNumber),
  );
  // Estado para detectar números especiais (0800, 4003, 400X, 300X)
  const [isSpecialNumber, setIsSpecialNumber] = React.useState(false);

  // Sincroniza estados internos quando o `value` externo muda.
  // Se o `value` carregar mais dígitos do que a máscara permite, faz
  // autocorreção retroativa via onChange para que o parent passe a
  // armazenar a versão saneada.
  React.useEffect(() => {
    const allDigits = (value || '').replace(/\D/g, '');
    const isSpecial = isSpecialPhoneNumber(allDigits);
    setIsSpecialNumber(isSpecial);

    if (isSpecial) {
      // Números especiais: usa valor diretamente (sem código de país).
      const { masked, safeDigits } = maskAndExtractSafeDigits(allDigits);
      setPhoneNumber(masked);
      setSelectedCode('+55'); // Mantém padrão para quando voltar a ser normal

      // Autocorreção: se o value externo era maior que safeDigits, reescreve.
      if (onChange && safeDigits !== allDigits) {
        onChange(safeDigits);
      }
    } else {
      // Números normais: extrai código de país e re-aplica a máscara.
      const extracted = extractCountryCode(value);
      const { masked, safeDigits } = maskAndExtractSafeDigits(
        extracted.localNumber,
      );
      setSelectedCode(extracted.countryCode);
      setPhoneNumber(masked);

      // Autocorreção: o value externo deve ser sempre `${countryCode}${safeDigits}`.
      // Se trouxe lixo a mais, normaliza no parent.
      if (onChange) {
        const expected = safeDigits ? `${extracted.countryCode}${safeDigits}` : '';
        const currentDigits = (value || '').replace(/[^0-9+]/g, '');
        // Compara em forma canônica (sem espaços/parênteses).
        const currentCanonical = currentDigits.startsWith('+')
          ? currentDigits
          : currentDigits
          ? `+${currentDigits}`
          : '';
        const expectedCanonical = expected
          ? expected.startsWith('+') ? expected : `+${expected}`
          : '';
        if (currentCanonical !== expectedCanonical) {
          onChange(expected);
        }
      }
    }
    // Intencional: rodar apenas quando `value` mudar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Handler para mudança no código de país
  const handleCountryCodeChange = (newCode: string) => {
    setSelectedCode(newCode);
    if (onChange) {
      // Usa SEMPRE os dígitos do que está visível (já mascarado), nunca o raw.
      const safeDigits = phoneNumber.replace(/\D/g, '');
      onChange(safeDigits ? `${newCode}${safeDigits}` : '');
    }
  };

  // Handler para mudança no número de telefone
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Aplica máscara e extrai dígitos JÁ truncados pela máscara.
    // Isso elimina a possibilidade de o backend receber mais dígitos do que
    // o usuário vê na tela (causa raiz do bug de "dígito extra repetido").
    const { masked, safeDigits } = maskAndExtractSafeDigits(rawValue);

    // Detecta se é número especial (0800, 4003, 400X, 300X) usando os
    // dígitos já saneados.
    const isSpecial = isSpecialPhoneNumber(safeDigits);
    setIsSpecialNumber(isSpecial);
    setPhoneNumber(masked);

    if (onChange) {
      if (isSpecial) {
        // Especiais: sem +55, apenas dígitos.
        onChange(safeDigits);
      } else {
        // Normais: concatena o código de país com os dígitos seguros.
        onChange(safeDigits ? `${selectedCode}${safeDigits}` : '');
      }
    }
  };

  // Placeholder dinâmico baseado no tipo de número
  const dynamicPlaceholder = isSpecialNumber ? '0800-XXX-XXXX' : placeholder;

  return (
    <div className={cn('flex gap-2', className)}>
      {/* Seletor de código de país - OCULTO para números especiais */}
      {!isSpecialNumber && (
        <Select
          value={selectedCode}
          onValueChange={handleCountryCodeChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              compactSelector ? 'w-[56px] px-2 justify-center' : 'w-[90px] px-2',
              'shrink-0'
            )}
          >
            {compactSelector ? (
              <span className="text-base leading-none">
                {countryCodes.find((c) => c.code === selectedCode)?.flag ?? '🌐'}
              </span>
            ) : (
              <SelectValue />
            )}
          </SelectTrigger>
          <SelectContent>
            {countryCodes.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                <span className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span>{country.code}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Campo de telefone (DDD + número) */}
      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        placeholder={dynamicPlaceholder}
        value={phoneNumber}
        onChange={handlePhoneChange}
        disabled={disabled}
        className="flex-1"
        aria-invalid={rest['aria-invalid']}
      />
    </div>
  );
}
